import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import { productionTasksAPI } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

// Chat-style comment thread on one "to produce" note, between sender and
// recipient (admins can join). Polls while open so replies show up live;
// opening it marks the thread as read.
const POLL_MS = 10000;

const TaskThread = ({ taskId, onClose, onChanged }) => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const dateLocale = language === 'de' ? 'de-DE' : 'en-GB';
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const lastCount = useRef(0);

  const load = useCallback(
    async (silent = false) => {
      if (!taskId) return;
      if (!silent) setLoading(true);
      try {
        const { data } = await productionTasksAPI.getComments(taskId);
        setTask(data.data.task);
        setComments(data.data.comments);
        onChanged?.();
      } catch (err) {
        if (!silent) console.error('Failed to load comments:', err);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [taskId, onChanged],
  );

  useEffect(() => {
    setTask(null);
    setComments([]);
    setMessage('');
    lastCount.current = 0;
    load();
  }, [load]);

  useEffect(() => {
    if (!taskId) return undefined;
    const timer = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(timer);
  }, [taskId, load]);

  // Scroll to the newest message whenever one arrives.
  useEffect(() => {
    if (comments.length !== lastCount.current) {
      lastCount.current = comments.length;
      bottomRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [comments]);

  const send = async () => {
    const body = message.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const { data } = await productionTasksAPI.addComment(taskId, body);
      setComments((prev) => [...prev, data.data]);
      setMessage('');
      onChanged?.();
    } catch (err) {
      alert(t('productionTasks.failedToSend', { error: err.response?.data?.error || err.message }));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    // Enter sends, Shift+Enter makes a new line.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const itemTitle = task
    ? task.item_type === 'part'
      ? task.part_name
      : task.item_type === 'component'
        ? task.component_name
        : t('productionTasks.type_text')
    : '';

  return (
    <Modal isOpen={!!taskId} onClose={onClose} title={`💬 ${itemTitle || t('productionTasks.comments')}`}>
      {loading && !task ? (
        <LoadingSpinner />
      ) : !task ? null : (
        <div className='flex flex-col gap-4'>
          <div className='rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm'>
            <div className='flex flex-wrap gap-x-4 gap-y-1 text-gray-600'>
              <span>
                {t('productionTasks.colFrom')}: <b className='text-gray-800'>{task.created_by_name}</b>
              </span>
              <span>
                {t('productionTasks.colTo')}: <b className='text-gray-800'>{task.assigned_to_name}</b>
              </span>
              {task.quantity !== null && (
                <span>
                  {t('productionTasks.colQuantity')}: <b className='text-gray-800'>{task.quantity}</b>
                </span>
              )}
              <span>
                {t('productionTasks.colStatus')}:{' '}
                <b className='text-gray-800'>{t(`productionTasks.status_${task.status}`)}</b>
              </span>
            </div>
            {task.note && <p className='mt-2 text-gray-800 whitespace-pre-wrap'>{task.note}</p>}
          </div>

          <div className='max-h-[45vh] overflow-y-auto space-y-3 pr-1'>
            {comments.length === 0 ? (
              <p className='text-sm text-gray-500 text-center py-6'>{t('productionTasks.noComments')}</p>
            ) : (
              comments.map((c) => {
                const mine = c.user_id === user?.id;
                return (
                  <div key={c.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                        mine ? 'bg-[#361826] text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                      }`}
                    >
                      {!mine && <p className='text-xs font-semibold mb-0.5'>{c.user_name || c.user_login}</p>}
                      <p className='whitespace-pre-wrap break-words'>{c.body}</p>
                      <p className={`text-[10px] mt-1 ${mine ? 'text-gray-300' : 'text-gray-400'}`}>
                        {new Date(c.created_at).toLocaleString(dateLocale)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          <div className='flex items-end gap-2 border-t border-gray-100 pt-3'>
            <textarea
              rows={2}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={onKeyDown}
              className='input-field flex-1 resize-none'
              placeholder={t('productionTasks.writeMessage')}
              autoFocus
            />
            <button onClick={send} disabled={sending || !message.trim()} className='btn-primary whitespace-nowrap'>
              {sending ? t('productionTasks.sending') : t('productionTasks.send')}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default TaskThread;
